import { SettingsActions, ISettingsAction } from "@actions/settings.actions";
import * as ISchema from "@schema";

export interface ISettingsState {
  userTeamCalendarIds: string[];
  expandAccount: boolean;
  expandHours: boolean;
  expandMeetingBreaks: boolean;
  expandTeam: boolean;
  expandFeatures: boolean;
  expandColorCoding: boolean;
  expandRooms: boolean;
  lastRoomSettingsMillis: number;
  expandIntegrations: boolean;
  expandInvites: boolean;
  expandExperiments: boolean;
  expandSmartHolds: boolean;
  expandPersonalCalendar: boolean;
  expandLunch: boolean;
  expandTravelTime: boolean;
  expandFocusTime: boolean;
  expandEmailsAndNotifications: boolean;
  expandOrgSettings: boolean;
  expandPlansAndBillingSettings: boolean;
  closeAfterSaving: boolean;
  requiresSaveHours: boolean;
  triggerSaveHoursMillis: number;
  requiresSaveFocusTimeSync: boolean;
  triggerSaveFocusTimeSyncMillis: number;
  requiresSaveLunchSync: boolean;
  triggerSaveLunchSyncMillis: number;
  requiresSaveTravelTimeSync: boolean;
  triggerSaveTravelTimeSyncMillis: number;
  pendingSendInvite: string;
  teamSettingsId: string;
  teamSettingsSubTabIndex: number;
  teamSettingsCreateOrJoin: string;
  expandPaidPlans: boolean;
  teamSettingsFeature: string;
  schedulingLinks: ISchema.ISchedulingLink[];
  linkName: string;
  savedOnboardingChecklistSyncType: "";
}

export const initialExpansionStates = {
  expandAccount: false,
  expandHours: false,
  expandMeetingBreaks: false,
  expandTeam: false,
  expandFeatures: false,
  expandColorCoding: false,
  expandRooms: false,
  expandIntegrations: false,
  expandInvites: false,
  expandExperiments: false,
  expandSmartHolds: false,
  expandPersonalCalendar: false,
  expandLunch: false,
  expandTravelTime: false,
  expandFocusTime: false,
  expandEmailsAndNotifications: false,
  expandPaidPlans: false,
  expandOrgSettings: false,
  expandPlansAndBillingSettings: false,
};

export const initialSettingsState: ISettingsState = {
  userTeamCalendarIds: [],
  lastRoomSettingsMillis: 0,
  closeAfterSaving: false,
  requiresSaveHours: false,
  triggerSaveHoursMillis: 0,
  requiresSaveFocusTimeSync: false,
  triggerSaveFocusTimeSyncMillis: 0,
  requiresSaveLunchSync: false,
  triggerSaveLunchSyncMillis: 0,
  requiresSaveTravelTimeSync: false,
  triggerSaveTravelTimeSyncMillis: 0,
  pendingSendInvite: "",
  ...initialExpansionStates,
  teamSettingsId: "",
  teamSettingsSubTabIndex: -1,
  teamSettingsFeature: "",
  teamSettingsCreateOrJoin: "",
  schedulingLinks: [],
  linkName: "",
  savedOnboardingChecklistSyncType: "",
};

export function settingsReducer(state = initialSettingsState, action: ISettingsAction) {
  switch (action.type) {
    case SettingsActions.SetUserTeam:
      return { ...state, userTeamCalendarIds: action.data };
    case SettingsActions.OpenLunch:
      return {
        ...state,
        ...initialExpansionStates,
        expandLunch: true,
        expandHours: true,
      };
    case SettingsActions.OpenTeam:
      return {
        ...state,
        ...initialExpansionStates,
        expandTeam: true,
        teamSettingsId: action.data.teamSettingsId,
        teamSettingsSubTabIndex: action.data.subTabIndex,
        teamSettingsFeature: action.data.teamSettingsFeature,
        teamSettingsCreateOrJoin: action.data.teamSettingsCreateOrJoin,
      };
    case SettingsActions.ResetTeamId:
      return {
        ...state,
        teamSettingsId: "",
        teamSettingsSubTabIndex: -1,
        teamSettingsFeature: "",
        teamSettingsCreateOrJoin: "",
      };
    case SettingsActions.OpenPersonalCalendar:
      return {
        ...state,
        ...initialExpansionStates,
        expandPersonalCalendar: true,
        expandFeatures: true,
      };
    case SettingsActions.OpenSmartHolds:
      return { ...state, ...initialExpansionStates, expandSmartHolds: true };
    case SettingsActions.OpenPaidPlans:
      return { ...state, ...initialExpansionStates, expandPaidPlans: true };
    case SettingsActions.UpdatePreferredRoomsMillis:
      const now = new Date();
      return { ...state, lastRoomSettingsMillis: now.valueOf() };
    case SettingsActions.OpenIntegrations:
      return { ...state, ...initialExpansionStates, expandIntegrations: true };
    case SettingsActions.OpenInvites:
      return { ...state, ...initialExpansionStates, expandInvites: true };
    case SettingsActions.OpenSmartHolds:
      return { ...state, ...initialExpansionStates, expandSmartHolds: true };
    case SettingsActions.CloseSettingsDrawers:
      return { ...state, ...initialExpansionStates };
    case SettingsActions.OpenRooms:
      return {
        ...state,
        ...initialExpansionStates,
        expandRooms: true,
        expandHours: true,
      };
    case SettingsActions.ToggleExpanded:
      const expanded = !state[action.data as keyof ISettingsState];
      let expandFeatures = false;
      let expandSmartHolds = false;
      let expandHours = false;
      let expandIntegrations = false;
      if (action.data === "expandColorCoding") {
        expandFeatures = true;
      }
      if (
        action.data === "expandFocusTime" ||
        action.data === "expandLunch" ||
        action.data === "expandTravelTime" ||
        action.data === "expandPersonalCalendar"
      ) {
        expandSmartHolds = true;
      }
      if (action.data === "expandMeetingBreaks") {
        expandHours = true;
      }
      if (action.data === "expandIntegrations") {
        expandIntegrations = true;
      }
      return {
        ...state,
        ...initialExpansionStates,
        expandHours,
        expandFeatures,
        expandSmartHolds,
        expandIntegrations,
        [action.data]: expanded,
      };
    case SettingsActions.SetCloseAfterSaving:
      return { ...state, closeAfterSaving: action.data };
    case SettingsActions.SetRequireSaveHours:
      return { ...state, requiresSaveHours: action.data };
    case SettingsActions.TriggerSaveHours:
      return { ...state, triggerSaveHoursMillis: new Date().valueOf() };
    case SettingsActions.SetRequireSaveFocusTimeSync:
      return { ...state, requiresSaveFocusTimeSync: action.data };
    case SettingsActions.TriggerSaveFocusTimeSync:
      return { ...state, triggerSaveFocusTimeSyncMillis: new Date().valueOf() };
    case SettingsActions.SetRequireSaveLunchSync:
      return { ...state, requiresSaveLunchSync: action.data };
    case SettingsActions.TriggerSaveLunchSync:
      return { ...state, triggerSaveLunchSyncMillis: new Date().valueOf() };
    case SettingsActions.SetRequireSaveTravelTimeSync:
      return { ...state, requiresSaveTravelTimeSync: action.data };
    case SettingsActions.TriggerSaveTravelTimeSync:
      return {
        ...state,
        triggerSaveTravelTimeSyncMillis: new Date().valueOf(),
      };
    case SettingsActions.SendInvite:
      return { ...state, pendingSendInvite: action.data };
    case SettingsActions.SetSchedulingLinks:
      return { ...state, schedulingLinks: [...action.data] };
    case SettingsActions.SetLinkName:
      return { ...state, linkName: action.data };
    case SettingsActions.SetSavedOnboardingChecklistSyncType:
      return { ...state, savedOnboardingChecklistSyncType: action.data };
    default:
      return state;
  }
}
